import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { getEvents } from "../api/eventsApi";
import { format } from "date-fns";
import { parse } from "date-fns";

const normalizeEvent = (raw, index) => {
  if (!raw || typeof raw !== "object") return null;

  const title = raw.title ? String(raw.title).trim() : "Untitled Event";
  const description = raw.description ? String(raw.description).trim() : "";
  const location = raw.location ? String(raw.location).trim() : "";
  const imageUrl = raw.imageUrl || raw.img || "";
  const otherImages =
    raw.otherImages ||
    raw.OtherImages ||
    ""
      .split(",")
      .map((url) => url.trim())
      .filter(Boolean); // always an array now;

  let dateObj = null;
  if (raw.date) {
    const d = parse(raw.date, "dd/MM/yyyy", new Date());
    if (!isNaN(d.getTime())) dateObj = d;
  }

  return {
    id: raw.id || index,
    title,
    description,
    location,
    imageUrl,
    dateObj,
    otherImages,
  };
};

const FeaturedEvents = () => {
  const [eventList, setEventList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  const handleSeeMore = (event) => {
    navigate(`/events/${event.id}`, { state: event });
  };

  useEffect(() => {
    let mounted = true;
    const fetchEvents = async () => {
      try {
        setLoading(true);
        setError("");
        const response = await getEvents();
        if (!response.success) return;
        if (mounted) {
          const normalized = response.data
            .map((e, i) => normalizeEvent(e, i))
            .filter(Boolean);

          const now = new Date();
          now.setHours(0, 0, 0, 0);

          // Filter today + upcoming
          const filtered = normalized.filter(
            (e) => e.dateObj && e.dateObj >= now
          );

          // Sort: today first, then upcoming
          filtered.sort((a, b) => {
            const aTime = a.dateObj.setHours(0, 0, 0, 0);
            const bTime = b.dateObj.setHours(0, 0, 0, 0);
            const todayTime = now.getTime();
            if (aTime === todayTime && bTime !== todayTime) return -1;
            if (bTime === todayTime && aTime !== todayTime) return 1;
            return aTime - bTime;
          });

          setEventList(filtered.slice(0, 3)); // Limit to 3 upcoming events
        }
      } catch (err) {
        if (mounted) setError("Failed to load events. Please try again later.");
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchEvents();
    return () => {
      mounted = false;
    };
  }, []);

  return (
    <div className="w-full flex flex-col h-full">
      <h2 className="text-4xl font-bold text-yellow-400 mb-6">
        Upcoming Events
      </h2>

      {loading && (
        <div className="text-center text-gray-400 py-8">Loading events...</div>
      )}
      {error && !loading && (
        <div className="text-center text-red-400 py-8">{error}</div>
      )}
      {!loading && !error && eventList.length === 0 && (
        <div className="text-center text-gray-400 py-8">
          No upcoming events.
        </div>
      )}

      {!loading && !error && eventList.length > 0 && (
        <div className="flex flex-col space-y-4 flex-grow min-h-[380px]">
          {eventList.map((event) => (
            <div
              key={event.id}
              className="flex cursor-pointer hover:bg-gray-800/30 p-3 rounded-lg transition-colors duration-300 h-32"
              onClick={() => handleSeeMore(event)}
            >
              {/* Date Box */}
              <div className="flex-shrink-0 w-20 h-20 bg-amber-600 text-white rounded-lg flex flex-col items-center justify-center text-center mr-4">
                <span className="text-xs">
                  {event.dateObj && format(event.dateObj, "MMM")}
                </span>
                <span className="text-2xl font-bold">
                  {event.dateObj && format(event.dateObj, "dd")}
                </span>
              </div>

              {/* Event Details */}
              <div>
                <h3 className="text-xl font-semibold text-white">
                  {event.title}
                </h3>
                <p className="text-gray-300 line-clamp-2 text-sm">
                  {event.description}
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-auto pt-6 text-center">
        <Link
          to="/events"
          onClick={() => window.scrollTo(0, 0)}
          className="group inline-flex items-center justify-center gap-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white font-semibold rounded-full hover:from-amber-700 hover:to-amber-600 transform transition-all duration-300 hover:scale-105 shadow-lg hover:shadow-amber-500/30 border border-amber-500/20 min-w-[180px]"
        >
          <span>View All Events</span>
          <svg
            className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M13 7l5 5m0 0l-5 5m5-5H6"
            />
          </svg>
        </Link>
      </div>
    </div>
  );
};

export default FeaturedEvents;
